import { Stack, useTheme, Portal } from "@mui/material";
import React, { useEffect, useState } from "react";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import { trackEvent, ANALYTICS_EVENTS } from "@analytics";
import useAppStore from "../../../stores/appStore";
import {
  openWidgetInitiallySelector,
  previousSystemModeSelector, setHeaderSkeletonSelector,
  setIsWebSnippetOpenSelector,
  setSystemModeSelector,
  hasPermissionSelector,
  selectedAssistantSelector,
  assistantIdSelector
} from "../../../stores/selectors/appSelectors";
import { pollPreviewStatusSelector } from "src/stores/selectors/previewSelectors";
import DashboardHyroLogo from "../../Logos/DashboardHyroLogo";
import { PreviewBannerContainer, StyledButton } from "../HeaderStyles";
import ShareIcon from "src/icons/ShareIcon";
import SharePreviewDialog from "../preview/SharePreviewDialog";
import { TOOLTIP_DELAY } from "@utils";
import { ASSISTANT_ENVIRONMENT, SEVERITY_VARIANTS } from "@consts";
import Tooltip from "@hyro-components/Tooltip";
import { SHARE_PREVIEW_TOOLTIP_TITLE } from "@phrasing";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import DevBanner from "src/layout/Header/header-types/dev-banner/DevBanner";
import useExtendPreview from "../preview/extend-preview/ExtendPreview.hook";
import useSnackbar from "@hyro-components/hooks/useSnackbar";
import HyroFeedbackMessage from "@hyro-components/HyroFeedbackMessage";
import ExtendPreviewBanner from "../preview/extend-preview/ExtendPreviewBanner";
import { BANNER_VARIANTS } from "../preview/extend-preview/ExtendPreviewBanner.consts";
import { SNIPPET_Z_INDEX } from "src/components/widgets/overview/Snippet";

const PreviewModeHeader = () => {
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const setIsWebSnippetOpen = useAppStore(setIsWebSnippetOpenSelector);
  const setSystemMode = useAppStore(setSystemModeSelector);
  const previousSystemMode = useAppStore(previousSystemModeSelector);
  const setShowHeaderSkeleton = useAppStore(setHeaderSkeletonSelector);
  const openWidgetInitially = useAppStore(openWidgetInitiallySelector);
  const hasPermission = useAppStore(hasPermissionSelector);
  const assistantId = useAppStore(assistantIdSelector);
  const pollPreviewStatus = useAppStore(pollPreviewStatusSelector);
  const [isSharePreviewDialogOpen, setIsSharePreviewDialogOpen] = useState(false);
  const theme = useTheme();
  const isShareable = selectedAssistant?.environment === ASSISTANT_ENVIRONMENT.PRODUCTION || previousSystemMode === SYSTEM_MODES.EDIT;

  const [
    isSnackbarOpen,
    snackbarMessage,
    addSnackbarMessage,
    exitSnackbar,
    closeSnackbar
  ] = useSnackbar();
  const [snackbarSeverity, setSnackbarSeverity] = useState(SEVERITY_VARIANTS.SUCCESS);

  const { isExtendingPreview, handleExtendPreview, getExpirationText } = useExtendPreview(addSnackbarMessage, setSnackbarSeverity);

  useEffect(() => {
    setShowHeaderSkeleton(false);

    if (openWidgetInitially) {
      trackEvent(ANALYTICS_EVENTS.GENERAL.TOGGLE_WIDGET, { toggleState: "Opened" });
      setIsWebSnippetOpen(true);
    }

  }, [openWidgetInitially, setIsWebSnippetOpen, setShowHeaderSkeleton]);

  useEffect(() => {
    if (assistantId && openWidgetInitially) {
      const isLivePreview = previousSystemMode !== SYSTEM_MODES.EDIT;
      pollPreviewStatus({ isLivePreview });
    }
  }, [assistantId, openWidgetInitially, pollPreviewStatus, previousSystemMode]);

  const handleExitPreviewMode = () => {
    trackEvent(ANALYTICS_EVENTS.GENERAL.TOGGLE_WIDGET, { toggleState: "Closed" });
    setSystemMode(previousSystemMode);
    setIsWebSnippetOpen(false);
  };

  return <Stack direction="column">
    <Stack direction='row'>
      <DashboardHyroLogo disabled={true}/>
      <Stack direction='row' sx={{ ml: "auto" }} alignItems={"center"} justifyContent='center' gap={1}>
        {hasPermission(USER_PERMISSIONS.SHARE_PREVIEW.READ) &&
          <Tooltip title={isShareable ? "" : SHARE_PREVIEW_TOOLTIP_TITLE} placement="bottom" tooltipStyle={{ maxWidth: "150px" }} enterDelay={TOOLTIP_DELAY.LONG} enterNextDelay={TOOLTIP_DELAY.LONG}>
            <StyledButton
              onClick={() => setIsSharePreviewDialogOpen(true)}
              disabled={!isShareable}
              sx={{ gap: "8px" }}
              variant="outlined">
              <ShareIcon stroke={isShareable ? theme.palette.primary[500] : theme.palette.grey[400]} /> Share
            </StyledButton>
          </Tooltip>}

        <StyledButton
          onClick={handleExitPreviewMode}
          variant="outlined">
          Exit
        </StyledButton>
      </Stack>
      <DevBanner />
      <SharePreviewDialog isSharePreviewDialogOpen={isSharePreviewDialogOpen} setIsSharePreviewDialogOpen={setIsSharePreviewDialogOpen} />
    </Stack>

    <PreviewBannerContainer>
      <ExtendPreviewBanner
        variant={BANNER_VARIANTS.HEADER}
        getExpirationText={getExpirationText}
        handleExtendPreview={handleExtendPreview}
        isExtendingPreview={isExtendingPreview}
      />
    </PreviewBannerContainer>

    <Portal>
      <HyroFeedbackMessage
        sx={{ zIndex: SNIPPET_Z_INDEX + 1 }}
        message={snackbarMessage}
        severity={snackbarSeverity}
        showFeedback={isSnackbarOpen}
        onExited={exitSnackbar}
        closeFeedback={closeSnackbar}
      />
    </Portal>
  </Stack>;
};

export default PreviewModeHeader;
